import { useContext, useEffect, useRef, useState } from 'react';
import { Tree } from 'primereact/tree';
import { Dialog } from '../dialogs/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ExplainedButton as Button } from '../explained-button';
import { GeneralErrorDialog } from '../dialogs/error-dialog';
import { useApi } from '../../providers/api-provider';
import { UserManagementDataContext } from '../../providers/user-management-provider';
import { useBookmarkSelection } from '../../general/bookmark-selection';
import { useUnsavedActions } from '../../general/unsaved-changes-context';
import { UserDto, UserGroupsDto } from '../../general/dtos';
import { UserList } from './user-list';
import { USER_GROUP_DRAG_TYPE } from '../../general/user-group-drag';
import '../../assets/user-groups.css';

type GroupSelection = number | 'ungrouped' | null;
export function UserDirectory({selectedUsers,onSelectionChange}:{selectedUsers:UserDto[];onSelectionChange:(users:UserDto[])=>void}) {
    const api=useApi();
    const {cancelEditing}=useContext(UserManagementDataContext)!;
    const {requestAction}=useUnsavedActions();
    const [bookmark,setBookmark]=useBookmarkSelection('group');
    const groupId:GroupSelection=bookmark==='ungrouped'?'ungrouped':bookmark && /^[1-9][0-9]*$/.test(bookmark) && Number.isSafeInteger(Number(bookmark))?Number(bookmark):null;
    const [groups,setGroups]=useState<UserGroupsDto>({data:[],counts:{all:0,ungrouped:0}});
    const [loaded,setLoaded]=useState(false);
    const [busy,setBusy]=useState(false);
    const [error,setError]=useState('');
    const [action,setAction]=useState<'create'|'rename'|'delete'|'move'|'remove'|null>(null);
    const [name,setName]=useState('');
    const [destination,setDestination]=useState<number|null>(null);
    const [refreshKey,setRefreshKey]=useState(0);
    const [expanded,setExpanded]=useState<Record<string,boolean>>({all:true});
    const [dropTarget,setDropTarget]=useState<string|null>(null);
    const inFlight=useRef(false);
    const mounted=useRef(true);
    useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
    useEffect(()=>{
        const clear=()=>setDropTarget(null);
        document.addEventListener('dragend',clear);
        return()=>document.removeEventListener('dragend',clear);
    },[]);
    const current=groups.data.find(group=>group.id===groupId);
    useEffect(()=>{
        if(!api)return;
        let active=true;
        api.getUserGroups().then(result=>{if(active){setGroups(result);setLoaded(true);}})
            .catch(failure=>{if(active)setError(failure.response?.data?.message||'Could not load user groups.');});
        return()=>{active=false;};
    },[api]);
    const open=(next:typeof action)=>requestAction(()=>{
        cancelEditing();setName(next==='create'?'':current?.name??'');setDestination(current?.id??groups.data[0]?.id??null);setAction(next);
    });
    const choose=(next:GroupSelection)=>{
        if(next===groupId || busy)return;
        requestAction(()=>{cancelEditing();onSelectionChange([]);setBookmark(next===null?null:String(next));});
    };
    const apply=async()=>{
        if(inFlight.current)return;
        inFlight.current=true;setBusy(true);setError('');
        try{
            let result:UserGroupsDto;
            if(action==='create') result=await api.createUserGroup(name.trim());
            else if(action==='rename' && current) result=await api.renameUserGroup(current.id,name.trim());
            else if(action==='delete' && current) result=await api.deleteUserGroup(current.id);
            else if(action==='move') result=await api.changeUserGroupMembers(destination,selectedUsers.map(user=>user.sub),'move');
            else if(action==='remove' && current) result=await api.changeUserGroupMembers(current.id,selectedUsers.map(user=>user.sub),'remove');
            else return;
            if(!mounted.current)return;
            setGroups(result);setLoaded(true);setAction(null);onSelectionChange([]);setRefreshKey(value=>value+1);
            if(action==='delete' && new URLSearchParams(window.location.search).get('group')===String(current?.id))setBookmark(null);
        }catch(failure:any){if(mounted.current)setError(failure.response?.data?.message||'The group operation could not be completed. Please try again.');}
        finally{inFlight.current=false;if(mounted.current)setBusy(false);}
    };
    const moveDroppedUsers=async(keys:string[],target:number|null)=>{
        if(inFlight.current)return;
        inFlight.current=true;setBusy(true);setError('');
        try{
            const result=await api.changeUserGroupMembers(target,keys,'move');
            if(!mounted.current)return;
            setGroups(result);onSelectionChange([]);setRefreshKey(value=>value+1);
        }catch(failure:any){if(mounted.current)setError(failure.response?.data?.message||'The users could not be moved. Please try again.');}
        finally{inFlight.current=false;if(mounted.current)setBusy(false);}
    };
    const rowGroupKey=(row:HTMLElement)=>row.querySelector<HTMLElement>('[data-user-group-id]')?.dataset.userGroupId;
    const dragOverGroup=(event:React.DragEvent<HTMLDivElement>)=>{
        if(busy||!loaded||!Array.from(event.dataTransfer.types).includes(USER_GROUP_DRAG_TYPE))return;
        const key=rowGroupKey(event.currentTarget);
        if(key===undefined||key==='all')return;
        event.preventDefault();event.stopPropagation();event.dataTransfer.dropEffect='move';setDropTarget(key);
    };
    const dropUsers=(event:React.DragEvent<HTMLDivElement>)=>{
        const key=rowGroupKey(event.currentTarget);
        const raw=event.dataTransfer.getData(USER_GROUP_DRAG_TYPE);
        if(key===undefined||key==='all'||!raw)return;
        event.preventDefault();event.stopPropagation();setDropTarget(null);
        if(busy||!loaded)return;
        try{
            const keys:unknown=JSON.parse(raw);
            if(!Array.isArray(keys)||!keys.length||keys.length>10000||!keys.every(value=>typeof value==='string'&&value.length>0))throw new Error();
            const target=key==='ungrouped'?null:Number(key);
            requestAction(()=>{cancelEditing();void moveDroppedUsers(keys,target);});
        }catch{setError('The dragged user selection is invalid. Select the users and try again.');}
    };
    const nodes=[{key:'all',label:`All users${loaded?` (${groups.counts.all})`:''}`,icon:'pi pi-users',children:[
        {key:'ungrouped',label:`Ungrouped${loaded?` (${groups.counts.ungrouped})`:''}`,icon:'pi pi-user',className:dropTarget==='ungrouped'?'user-group-drop-active':undefined},
        ...groups.data.map(group=>({key:String(group.id),label:`${group.name} (${group.user_count})`,icon:'pi pi-users',className:dropTarget===String(group.id)?'user-group-drop-active':undefined}))
    ]}];
    return <div className="user-directory">
        <section className="user-groups" aria-label="User groups" aria-busy={busy || !loaded}>
            <div className="admin-toolbar">
                <strong>Groups</strong>
                <Button label="New group" icon="pi pi-plus" text size="small" disabled={!loaded||busy}
                    disabledReason="Wait for the groups to finish loading." onClick={()=>open('create')}/>
                <Button label="Rename" text size="small" disabled={!current||busy} disabledReason="Select a group to rename." onClick={()=>open('rename')}/>
                <Button label="Delete group" text size="small" disabled={!current||busy} disabledReason="Select a group to delete." onClick={()=>open('delete')}/>
            </div>
            <Tree value={nodes} selectionMode="single" selectionKeys={String(groupId??'all')} expandedKeys={expanded}
                pt={{content:{className:'user-group-row',onDragEnterCapture:dragOverGroup,onDragOverCapture:dragOverGroup,
                    onDragLeaveCapture:(event:React.DragEvent<HTMLDivElement>)=>{
                        if(!(event.relatedTarget instanceof Node)||!event.currentTarget.contains(event.relatedTarget)){
                            const key=rowGroupKey(event.currentTarget);
                            setDropTarget(previous=>previous===key?null:previous);
                        }
                    },onDropCapture:dropUsers}}}
                nodeTemplate={node=><span className="user-group-target" data-user-group-id={node.key}>{node.label}</span>}
                onToggle={event=>setExpanded(event.value)} onSelectionChange={event=>{
                    if(!loaded||busy||event.value==null)return;
                    choose(event.value==='all'?null:event.value==='ungrouped'?'ungrouped':Number(event.value));
                }}/>
            <div className="admin-toolbar user-group-membership-actions">
                <Button label={`Move selected (${selectedUsers.length})`} text size="small" disabled={busy||!loaded||!groups.data.length||!selectedUsers.length}
                    disabledReason={!groups.data.length?'Create a group first.':'Select users to move to a group.'} onClick={()=>open('move')}/>
                <Button label={`Remove selected (${selectedUsers.length})`} text size="small" disabled={busy||!current||!selectedUsers.length}
                    disabledReason={!current?'Select a named group first.':'Select users to remove from this group.'} onClick={()=>open('remove')}/>
            </div>
            <small className="text-color-secondary">Drag users onto a group or Ungrouped to move them. Ctrl/Cmd or Shift selects multiple users.</small>
        </section>
        <div className="user-directory-list"><UserList selectedUsers={selectedUsers} onSelectionChange={onSelectionChange} groupId={groupId} refreshKey={refreshKey} allowGroupDrag/></div>
        <Dialog header={action==='create'?'New group':action==='rename'?'Rename group':action==='delete'?'Delete group':action==='move'?'Move users to group':'Remove users from group'}
            visible={action!==null} onHide={()=>{if(!busy)setAction(null);}} modal draggable={false} closable={!busy} closeOnEscape={!busy}
            style={{width:'30rem',maxWidth:'calc(100vw - 2rem)'}}
            footer={<><Button label="Cancel" text disabled={busy} onClick={()=>setAction(null)}/>
                <Button label={action==='delete'?'Delete':action==='move'?'Move':action==='remove'?'Remove':'Save'}
                    severity={action==='delete'?'danger':undefined} loading={busy} disabled={busy||((action==='create'||action==='rename')&&!name.trim())}
                    disabledReason={busy?'Wait for the group operation to finish.':'Enter a group name first.'}
                    onClick={()=>void apply()}/></>}>
            {(action==='create'||action==='rename')&&<><label htmlFor="user-group-name" className="block mb-2">Group name</label>
                <InputText id="user-group-name" value={name} onChange={event=>setName(event.target.value)} maxLength={250} autoFocus className="w-full" disabled={busy}/></>}
            {action==='move'&&<><label htmlFor="user-group-destination" className="block mb-2">Group</label>
                <Dropdown inputId="user-group-destination" value={destination} options={[{id:null,name:'Ungrouped'},...groups.data]} optionLabel="name" optionValue="id" filter className="w-full"
                    scrollHeight="min(28rem, 60vh)" onChange={event=>setDestination(event.value)} disabled={busy}/>
                <p className="text-color-secondary">The selected users will be moved from their current group to this destination.</p></>}
            {action==='remove'&&<p>Remove the {selectedUsers.length} selected user(s) from <strong>{current?.name}</strong>? Their accounts and course/module access will be kept.</p>}
            {action==='delete'&&<p>Delete <strong>{current?.name}</strong>? Its users will become ungrouped. User accounts and course/module access will be kept.</p>}
        </Dialog>
        <GeneralErrorDialog visible={!!error} message={error} title="User groups" onClose={()=>setError('')}/>
    </div>;
}
